import type {
    ClinicCollection,
    ClinicCounters,
    ClinicData,
    ClinicRecord,
    ClinicSettings
} from '@/lib/clinic/types'

import { createEmptyData, createSeedData } from '@/lib/clinic/seed'
import { parseCodeNumber } from '@/lib/clinic/ids'
import { nowIso } from '@/lib/clinic/datetime'

import { DEFAULT_SETTINGS } from '@/constants/clinic'

/**
 * Every read and write of clinic records goes through this interface.
 * The app currently ships a browser storage implementation. To move to a real
 * backend, implement the same methods against an API and swap the export at
 * the bottom of this file. Nothing in the UI needs to change.
 */
export interface ClinicRepository {
    load(): Promise<ClinicData>
    saveRecord<K extends ClinicCollection>(collection: K, record: ClinicRecord<K>): Promise<void>
    deleteRecords(collection: ClinicCollection, ids: string[]): Promise<void>
    saveSettings(settings: ClinicSettings): Promise<void>
    saveCounters(counters: ClinicCounters): Promise<void>
    replaceAll(data: ClinicData): Promise<void>
}

const STORAGE_PREFIX = 'dsc-clinic:v1:'

const STORAGE_KEYS = {
    meta: `${STORAGE_PREFIX}meta`,
    settings: `${STORAGE_PREFIX}settings`,
    counters: `${STORAGE_PREFIX}counters`,
    patients: `${STORAGE_PREFIX}patients`,
    consultations: `${STORAGE_PREFIX}consultations`,
    prescriptions: `${STORAGE_PREFIX}prescriptions`,
    followUps: `${STORAGE_PREFIX}follow-ups`,
    reminders: `${STORAGE_PREFIX}reminders`
} as const

const COLLECTIONS: ClinicCollection[] = ['patients', 'consultations', 'prescriptions', 'followUps', 'reminders']

export const isClinicStorageKey = (key: string): boolean => key.startsWith(STORAGE_PREFIX)

export const isStorageFullError = (error: unknown): boolean => {
    if (!(error instanceof DOMException)) return false

    return error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED' || error.code === 22
}

const getStorage = (): Storage => {
    if (typeof window === 'undefined') throw new Error('Clinic records can only be read in the browser.')

    return window.localStorage
}

const readJson = <T>(key: string): T | null => {
    const raw = getStorage().getItem(key)
    if (!raw) return null

    try {
        return JSON.parse(raw) as T
    }
    catch {
        return null
    }
}

const writeJson = (key: string, value: unknown): void => {
    getStorage().setItem(key, JSON.stringify(value))
}

const normalizeCounters = (data: ClinicData): ClinicCounters => {
    const maxPatient = data.patients.reduce((max, patient) => Math.max(max, parseCodeNumber(patient.code)), 0)
    const maxPrescription = data.prescriptions.reduce((max, prescription) => Math.max(max, parseCodeNumber(prescription.number)), 0)

    return {
        patient: Math.max(data.counters.patient, maxPatient),
        prescription: Math.max(data.counters.prescription, maxPrescription)
    }
}

const writeAll = (data: ClinicData): void => {
    COLLECTIONS.forEach(collection => writeJson(STORAGE_KEYS[collection], data[collection]))
    writeJson(STORAGE_KEYS.settings, data.settings)
    writeJson(STORAGE_KEYS.counters, data.counters)
    writeJson(STORAGE_KEYS.meta, { version: 1, updatedAt: nowIso() })
}

const readAll = (): ClinicData => {
    const empty = createEmptyData()
    const storedSettings = readJson<Partial<ClinicSettings>>(STORAGE_KEYS.settings) ?? {}

    const data: ClinicData = {
        patients: readJson(STORAGE_KEYS.patients) ?? [],
        consultations: readJson(STORAGE_KEYS.consultations) ?? [],
        prescriptions: readJson(STORAGE_KEYS.prescriptions) ?? [],
        followUps: readJson(STORAGE_KEYS.followUps) ?? [],
        reminders: readJson(STORAGE_KEYS.reminders) ?? [],
        settings: { ...DEFAULT_SETTINGS, ...empty.settings, ...storedSettings },
        counters: readJson<ClinicCounters>(STORAGE_KEYS.counters) ?? empty.counters
    }

    return { ...data, counters: normalizeCounters(data) }
}

export const createBrowserRepository = (): ClinicRepository => ({
    async load() {
        const meta = readJson<{ version: number }>(STORAGE_KEYS.meta)

        if (!meta) {
            const seeded = createSeedData(new Date())
            writeAll(seeded)

            return seeded
        }

        return readAll()
    },

    async saveRecord(collection, record) {
        const key = STORAGE_KEYS[collection]
        const list = readJson<Array<{ id: string }>>(key) ?? []
        const exists = list.some(item => item.id === record.id)
        const next = exists ? list.map(item => (item.id === record.id ? record : item)) : [record, ...list]

        writeJson(key, next)
    },

    async deleteRecords(collection, ids) {
        if (ids.length === 0) return

        const key = STORAGE_KEYS[collection]
        const remove = new Set(ids)
        const list = readJson<Array<{ id: string }>>(key) ?? []

        writeJson(key, list.filter(item => !remove.has(item.id)))
    },

    async saveSettings(settings) {
        writeJson(STORAGE_KEYS.settings, settings)
    },

    async saveCounters(counters) {
        const stored = readJson<ClinicCounters>(STORAGE_KEYS.counters)

        writeJson(STORAGE_KEYS.counters, {
            patient: Math.max(counters.patient, stored?.patient ?? 0),
            prescription: Math.max(counters.prescription, stored?.prescription ?? 0)
        })
    },

    async replaceAll(data) {
        writeAll(data)
    }
})

export const clinicRepository: ClinicRepository = createBrowserRepository()
